import { EventLogCard } from "@/components/features/matter/event-logs/EventLogCard";
import {
  Button,
  Card,
  CardHeader,
  Input,
  LoadingSpinner,
  Typography,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui";
import { FilterBar } from "@/components/ui/FilterBar/FilterBar";
import type { FilterOption } from "@/components/ui/FilterSelector/FilterSelector";
import { getUsersOptions } from "@/lib/data/react-query";
import { retrieveMassTortEventsInfiniteQueryOptions } from "@/lib/data/react-query/mass-torts/mass-tort-collections";
import { retrieveMatterEventsInfiniteQueryOptions } from "@/lib/data/react-query/matters/matter-event";
import {
  MATTER_EVENT_CATEGORY_OPTIONS,
  type MatterEventCategory,
} from "@/lib/data/static/matters/matter-event";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { type MatterEvent, type MatterSubItemType } from "@/lib/types";
import { convertToLocalFromDatabaseDateTime } from "@/lib/utils/dateUtils";
import {
  formatEventWithType,
  type EventWithType,
} from "@/lib/utils/eventUtils";
import { formatDateTime, formatUsersName } from "@/lib/utils/format";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { AnimatePresence, motion } from "framer-motion";
import Fuse from "fuse.js";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

interface EventLogsProps {
  matter?: {
    eventLogs?: MatterEvent[];
    id?: string | null;
    matterId?: string | null;
  };
  bareLayout?: boolean;
  massTortId?: string;
  subItem?: { id: string; type: MatterSubItemType };
}

const relatedEventSubjectTypes: Partial<Record<MatterSubItemType, string>> = {
  task: "matter_task",
};

export const EventLogs = ({
  matter,
  bareLayout = false,
  massTortId,
  subItem,
}: EventLogsProps) => {
  const matterId = massTortId ? "" : (matter?.id ?? matter?.matterId ?? "");
  const [query, setQuery] = useState("");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [selectedFilters, setSelectedFilters] = useState<
    Partial<Record<string, string[]>>
  >({});

  const debouncedQuery = useDebouncedValue(query.trim(), 250);
  const afterDate = selectedFilters.afterDate?.[0];
  const beforeDate = selectedFilters.beforeDate?.[0];
  const invalidDateRange = Boolean(
    afterDate && beforeDate && afterDate > beforeDate,
  );
  const request = useMemo(
    () => ({
      matterId,
      actorUserId: (selectedFilters.id ?? [])
        .filter((id) => !id.startsWith("integration:"))
        .map((id) => id.replace(/^user:/, "")),
      actorIntegrationAppId: (selectedFilters.id ?? [])
        .filter((id) => id.startsWith("integration:"))
        .map((id) => id.slice("integration:".length)),
      itemType: (selectedFilters.itemType ?? []) as MatterEventCategory[],
      dateCreatedAfter: afterDate
        ? dayjs(afterDate).startOf("day").toISOString()
        : undefined,
      dateCreatedBefore: beforeDate
        ? dayjs(beforeDate).endOf("day").toISOString()
        : undefined,
      search: debouncedQuery || undefined,
      sortDirection,
    }),
    [
      matterId,
      selectedFilters,
      afterDate,
      beforeDate,
      debouncedQuery,
      sortDirection,
    ],
  );
  const matterEventsQuery = useInfiniteQuery({
    ...retrieveMatterEventsInfiniteQueryOptions(request),
    enabled: Boolean(matterId) && !invalidDateRange,
  });
  const massTortEventsQuery = useInfiniteQuery({
    ...retrieveMassTortEventsInfiniteQueryOptions({
      massTortId: massTortId ?? "",
      pageSize: 50,
    }),
    enabled: Boolean(massTortId),
  });
  const {
    data: eventLogsResponse,
    fetchNextPage,
    refetch,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isFetchNextPageError,
    isError: isEventsError,
    isLoading: isLoadingEvents,
  } = massTortId ? massTortEventsQuery : matterEventsQuery;
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { data: users } = useQuery(getUsersOptions());
  const getUserForUserId = useCallback(
    (id: string) => {
      const user = users?.data.find((user) => user.id === id);
      const userName = user
        ? `${user.firstName} ${user.lastName}`
        : "Unknown user";
      return { userName, user };
    },
    [users],
  );

  const eventsWithType = useMemo<EventWithType[]>(() => {
    const events = eventLogsResponse?.pages.flatMap((page) => page.data) ?? [];
    return events
      .map((event) => formatEventWithType({ event }))
      .filter((event) => {
        if (!subItem || event.itemId === subItem.id) return true;

        const relatedSubjectType = relatedEventSubjectTypes[subItem.type];
        return Boolean(
          relatedSubjectType &&
            event.subjects?.some(
              (subject) =>
                subject.type === relatedSubjectType &&
                subject.id === subItem.id,
            ),
        );
      });
  }, [eventLogsResponse?.pages, subItem]);

  const handleAddFilter = useCallback((key: string, value: string) => {
    if (!value) {
      return;
    }

    setSelectedFilters((prev) => {
      const existingValues = prev[key] ?? [];
      const isSingleValueKey = key === "afterDate" || key === "beforeDate";

      if (!isSingleValueKey && existingValues.includes(value)) {
        return prev;
      }

      return {
        ...prev,
        [key]: isSingleValueKey ? [value] : [...existingValues, value],
      };
    });
  }, []);

  const handleRemoveFilter = useCallback((key: string, value: string) => {
    setSelectedFilters((prev) => {
      const existingValues = prev[key] ?? [];
      const nextValues = existingValues.filter((current) => current !== value);

      if (nextValues.length === 0) {
        const entries = Object.entries(prev).filter(
          ([filterKey]) => filterKey !== key,
        );
        return Object.fromEntries(entries) as Partial<Record<string, string[]>>;
      }

      return {
        ...prev,
        [key]: nextValues,
      };
    });
  }, []);

  const hasActiveFilters = useMemo(() => {
    return Object.values(selectedFilters).some(
      (values) => Array.isArray(values) && values.length > 0,
    );
  }, [selectedFilters]);

  const [knownActors, setKnownActors] = useState<Record<string, string>>({});
  useEffect(() => {
    setKnownActors({});
  }, [matterId, massTortId]);
  useEffect(() => {
    const actors: Record<string, string> = {};
    eventsWithType.forEach((event) => {
      const integrationId = event.eventMetadata?.externalActorId;
      const userId = event.actorUserId ?? event.actor?.id;
      const isIntegration =
        typeof integrationId === "string" && integrationId.length > 0;
      const id = isIntegration ? integrationId : userId;
      if (!id) return;
      const key = massTortId
        ? id
        : `${isIntegration ? "integration" : "user"}:${id}`;
      actors[key] =
        formatUsersName(event.actor) ??
        (isIntegration ? "Integration" : getUserForUserId(id).userName);
    });
    setKnownActors((previous) => {
      if (
        Object.entries(actors).every(([key, label]) => previous[key] === label)
      )
        return previous;
      return { ...previous, ...actors };
    });
  }, [eventsWithType, massTortId, getUserForUserId]);
  const userFilterItems = useMemo(() => {
    const actors = new Map(Object.entries(knownActors));
    if (!massTortId) {
      users?.data.forEach((user) =>
        actors.set(`user:${user.id}`, formatUsersName(user) ?? "Unknown user"),
      );
    }
    return Array.from(actors, ([value, label]) => ({ label, value })).sort(
      (a, b) => a.label.localeCompare(b.label),
    );
  }, [knownActors, massTortId, users]);

  const itemTypeFilterItems = useMemo(() => {
    if (!massTortId) return [...MATTER_EVENT_CATEGORY_OPTIONS];
    const types = new Set<string>();
    eventsWithType.forEach((event) => {
      if (event.type) {
        types.add(event.type);
      }
    });

    return Array.from(types)
      .sort((a, b) => a.localeCompare(b))
      .map((type) => ({
        label: type,
        value: type,
      }));
  }, [eventsWithType, massTortId]);

  const filterOptions = useMemo<FilterOption[]>(() => {
    const options: FilterOption[] = [];

    if (userFilterItems.length) {
      options.push({
        label: "Actor",
        value: "id",
        input: {
          type: "searchable-select",
          placeholder: "Select actor",
          items: userFilterItems,
        },
      });
    }

    if (itemTypeFilterItems.length) {
      options.push({
        label: "Item type",
        value: "itemType",
        input: {
          type: "searchable-select",
          placeholder: "Select item type",
          items: itemTypeFilterItems,
        },
      });
    }

    options.push({
      label: "Date",
      value: "date",
      input: {
        type: "date",
        placeholder: "Select date",
        conditionOptions: [
          { label: "Before", value: "beforeDate" },
          { label: "After", value: "afterDate" },
        ],
      },
    });

    return options;
  }, [itemTypeFilterItems, userFilterItems]);

  // Mass tort collections support pagination; their filters run on loaded pages.
  const filteredBySelectorAndDate = useMemo(() => {
    if (!massTortId) return eventsWithType;
    const { id: userIds = [], itemType: itemTypes = [] } = selectedFilters;
    const afterDateValue = (selectedFilters.afterDate ?? [])[0];
    const beforeDateValue = (selectedFilters.beforeDate ?? [])[0];
    const afterDateBoundary = afterDateValue
      ? dayjs(afterDateValue, "YYYY-MM-DD", true).startOf("day")
      : null;
    const beforeDateBoundary = beforeDateValue
      ? dayjs(beforeDateValue, "YYYY-MM-DD", true).endOf("day")
      : null;

    return eventsWithType.filter((event) => {
      const integrationId = event.eventMetadata?.externalActorId;
      const actorId =
        typeof integrationId === "string" && integrationId
          ? integrationId
          : (event.actorUserId ?? event.actor?.id ?? "");
      const matchesUser = !userIds.length || userIds.includes(actorId);

      const matchesItemType =
        !itemTypes.length || itemTypes.includes(event.type);

      if (!matchesUser || !matchesItemType) {
        return false;
      }

      const eventDate = dayjs(
        convertToLocalFromDatabaseDateTime(event.dateCreated) ??
          event.dateCreated,
      );

      if (!eventDate.isValid()) {
        return true;
      }

      if (afterDateBoundary && eventDate.isBefore(afterDateBoundary)) {
        return false;
      }

      if (beforeDateBoundary && eventDate.isAfter(beforeDateBoundary)) {
        return false;
      }

      return true;
    });
  }, [eventsWithType, selectedFilters, massTortId]);

  const filteredEvents = useMemo(() => {
    if (!massTortId) return filteredBySelectorAndDate;
    const sanitizedQuery = query.trim();

    if (!sanitizedQuery) return filteredBySelectorAndDate;
    const searchable = filteredBySelectorAndDate.map((event) => ({
      ...event,
      author: formatUsersName(event.actor),
      dateCreatedString: formatDateTime(
        convertToLocalFromDatabaseDateTime(event.dateCreated) ?? "",
      ),
    }));

    const fuse = new Fuse(searchable, {
      keys: [
        { name: "message", weight: 0.8 },
        { name: "author", weight: 0.2 },
        { name: "dateCreatedString", weight: 0.1 },
      ],
      threshold: 0.35,
      ignoreLocation: true,
      minMatchCharLength: 1,
      includeScore: false,
      useExtendedSearch: false,
    });
    return fuse.search(sanitizedQuery).map((result) => result.item);
  }, [filteredBySelectorAndDate, query, massTortId]);

  const [discoverFilterOptions, setDiscoverFilterOptions] = useState(false);
  useEffect(() => {
    setDiscoverFilterOptions(false);
  }, [massTortId]);
  const loadAllFilteredEvents = Boolean(
    massTortId && (discoverFilterOptions || debouncedQuery || hasActiveFilters),
  );
  useEffect(() => {
    if (
      loadAllFilteredEvents &&
      hasNextPage &&
      !isFetching &&
      !isEventsError &&
      !invalidDateRange
    )
      void fetchNextPage();
  }, [
    loadAllFilteredEvents,
    hasNextPage,
    isFetching,
    isEventsError,
    invalidDateRange,
    fetchNextPage,
  ]);

  useEffect(() => {
    scrollContainerRef.current?.scrollTo({ top: 0 });
  }, [request]);
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const loadNearBottom = () => {
      if (
        !loadAllFilteredEvents &&
        container.scrollHeight - container.scrollTop - container.clientHeight <
          200 &&
        hasNextPage &&
        !isFetching &&
        !isEventsError &&
        !invalidDateRange
      )
        void fetchNextPage();
    };
    loadNearBottom();
    container.addEventListener("scroll", loadNearBottom);
    return () => {
      container.removeEventListener("scroll", loadNearBottom);
    };
  }, [
    fetchNextPage,
    hasNextPage,
    isFetching,
    isEventsError,
    invalidDateRange,
    eventLogsResponse?.pages,
    loadAllFilteredEvents,
  ]);

  return (
    <div
      className={
        bareLayout
          ? "flex h-full min-h-0 flex-col gap-2 overflow-hidden"
          : "bg-accent flex h-full min-h-0 flex-col gap-2 overflow-hidden rounded-md p-4"
      }
    >
      <div className="mx-1 flex flex-shrink-0 flex-col gap-2">
        <div className="flex flex-col gap-3">
          <div
            className="flex flex-col"
            onFocusCapture={() => {
              if (massTortId) setDiscoverFilterOptions(true);
            }}
          >
            <FilterBar
              filterOptions={filterOptions}
              selectedFilters={selectedFilters}
              onAddFilter={handleAddFilter}
              onRemoveFilter={handleRemoveFilter}
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="min-w-32 flex-1">
              <Input
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value);
                }}
                maxLength={massTortId ? undefined : 200}
                placeholder="Search events"
              />
            </div>
            {!massTortId && (
              <Select
                value={sortDirection}
                onValueChange={(value) => {
                  setSortDirection(value === "asc" ? "asc" : "desc");
                }}
              >
                <SelectTrigger
                  aria-label="Event order"
                  className="w-36 shrink-0"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="desc">Newest first</SelectItem>
                  <SelectItem value="asc">Oldest first</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>
        </div>
      </div>
      {/* Search input */}

      <AnimatePresence mode="wait">
        <motion.div
          key="eventlog"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
          ref={scrollContainerRef}
          className="mt-4 flex-1 space-y-4 overflow-y-auto"
        >
          <div className="space-y-3 pr-2 pb-4">
            {invalidDateRange ? (
              <Typography className="text-destructive text-center">
                The after date must be on or before the before date.
              </Typography>
            ) : isLoadingEvents ? (
              <div className="flex min-h-32 items-center justify-center">
                <LoadingSpinner className="h-6 w-6" />
              </div>
            ) : filteredEvents.length === 0 && !isEventsError ? (
              loadAllFilteredEvents && hasNextPage ? (
                <div className="flex min-h-32 items-center justify-center gap-2 text-sm">
                  <LoadingSpinner className="h-5 w-5" />
                  Searching events…
                </div>
              ) : (
                <Card className="w-full border-2 border-dashed">
                  <CardHeader className="space-y-1">
                    <Typography className="text-center">
                      No events found
                    </Typography>
                    <Typography variant="subHeading" className="text-center">
                      {query || hasActiveFilters
                        ? "Try adjusting your search term"
                        : `Events will appear here as they are added to the ${massTortId ? "mass tort" : "matter"}`}
                    </Typography>
                  </CardHeader>
                </Card>
              )
            ) : (
              Array.isArray(filteredEvents) &&
              filteredEvents.map((event, index) => {
                return (
                  <EventLogCard
                    key={event.id || `event-${index}`}
                    event={event}
                    actor={event.actor}
                  />
                );
              })
            )}
          </div>
          {!invalidDateRange && isEventsError && (
            <div className="flex flex-col items-center gap-2 pb-3">
              <Typography className="text-destructive text-center">
                {isFetchNextPageError
                  ? "Could not load more events."
                  : "Could not load events."}
              </Typography>
              <Button
                variant="outline"
                size="sm"
                disabled={isFetching}
                onClick={() => {
                  if (isFetchNextPageError) void fetchNextPage();
                  else void refetch();
                }}
              >
                Try again
              </Button>
            </div>
          )}
          {isFetchingNextPage && (
            <div className="text-muted-foreground flex items-center justify-center gap-2 pb-3 text-sm">
              <LoadingSpinner className="h-5 w-5" />
              Loading more events…
            </div>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};
